import { Box } from '@mui/material'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { QueryKeyT } from 'src/api/reactQuery'
import { IBaseEntity } from 'src/generated-types'
import { CenteredSpinner } from '../Spinner'
import GenericTable, { GenericTableProps } from './GenericTable'
import { getInfiniteScrollTableHeight, isAutoHeightEnabled } from './InfiniteScrollTable.action'
import { useInfiniteScroll } from './useInfiniteScroll'
import { gridFittedHeights } from './GenericTable'

const findScrollableDescendant = (container: HTMLElement): Element | null => {
  const descendants = Array.from(container.querySelectorAll<HTMLElement>('*'))
  let firstScrollable: HTMLElement | null = null

  for (const descendant of descendants) {
    const { overflowY } = window.getComputedStyle(descendant)
    if (overflowY !== 'auto' && overflowY !== 'scroll') continue
    if (descendant.scrollHeight > descendant.clientHeight) return descendant
    firstScrollable = firstScrollable ?? descendant
  }

  return firstScrollable
}

interface InfiniteScrollTableProps<T extends IBaseEntity> extends Omit<GenericTableProps, 'data'> {
  queryUrl: string
  queryParams?: object
  pageSize?: number
  generateQueryKey?: (page: number, pageSize: number, fields?: string) => QueryKeyT | undefined
  enabled?: boolean
  scrollThreshold?: number
  resetTrigger?: any
  containerProps?: any
  scrollableTarget?: string
  emptyComponent?: ReactNode
  onDataChange?: (data: T[]) => void
  onTotalCountChange?: (totalCount: number) => void
  tableContainerProps?: any
  maxVisibleRowCount?: number
  spinnerHeight?: number
  setDataLength?: (length: number) => void
  bottomPadding?: string
}

const InfiniteScrollTable = <T extends IBaseEntity>({
  queryUrl,
  queryParams = {},
  pageSize = 20,
  generateQueryKey,
  enabled = true,
  scrollThreshold = 0.9,
  resetTrigger,
  containerProps = {},
  emptyComponent,
  onDataChange,
  onTotalCountChange,
  tableContainerProps = {},
  spinnerHeight,
  id,
  columns,
  defaultColumnOption,
  enableColumnOptions = false,
  onRowClick,
  autoHeight = false,
  height = 'calc(100vh - 180px)',
  getRowHeight = () => 44,
  maxVisibleRowCount,
  setDataLength,
  bottomPadding = '80px',
  rowsToFit,
  ...genericTableProps
}: InfiniteScrollTableProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const isFetchingRef = useRef(false)
  const [fields, setFields] = useState<string>((queryParams as any)?.fields ?? '')

  const handleGenerateQueryKey: (page: number, pageSize: number, fields?: string) => QueryKeyT | undefined = (
    page: number,
    pageSize: number,
  ) => {
    if (generateQueryKey) {
      return generateQueryKey(page, pageSize, fields)
    }
    return [queryUrl, { ...queryParams, fields: fields, page, pageSize }]
  }

  const { data, isLoading, isFetching, hasNextPage, reset, fetchNextPage, isFetchingNextPage, totalCount } =
    useInfiniteScroll<T>({
      queryUrl,
      queryParams: { ...queryParams, fields: 'id,' + fields },
      pageSize,
      generateQueryKey: handleGenerateQueryKey,
      enabled,
    })

  // Only enable autoHeight if autoHeight is true and if totalCount is less than maxVisibleRowCount (if provided)
  const enableAutoHeight = isAutoHeightEnabled(autoHeight, totalCount, maxVisibleRowCount)

  const fillSection = rowsToFit?.fill === true
  const fitted = fillSection ? gridFittedHeights(data, getRowHeight, rowsToFit?.min, rowsToFit?.max) : undefined
  const fillBoxProps = fitted ? { height: `${fitted.min}px`, minHeight: `${fitted.min}px`, flex: '1 0 auto' } : {}

  useEffect(() => {
    if (resetTrigger !== undefined) {
      reset()
    }
  }, [resetTrigger, reset])

  useEffect(() => {
    if (onDataChange) {
      onDataChange(data)
    }
    if (setDataLength) {
      setDataLength(data.length)
    }
  }, [data, onDataChange, setDataLength])

  useEffect(() => {
    if (onTotalCountChange && !isLoading && !isFetching) {
      onTotalCountChange(totalCount)
    }
  }, [totalCount, onTotalCountChange, isLoading, isFetching])

  useEffect(() => {
    isFetchingRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (isFetchingRef.current || !hasNextPage) {
        return
      }

      const target = event.target as HTMLDivElement
      const scrollTop = target.scrollTop
      const scrollHeight = target.scrollHeight
      const clientHeight = target.clientHeight

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      if (scrollPercentage >= scrollThreshold) {
        isFetchingRef.current = true
        fetchNextPage().finally(() => {
          setTimeout(() => {
            isFetchingRef.current = false
          }, 300)
        })
      }
    }

    const setupScrollListener = () => {
      const container = scrollContainerRef.current
      if (container) {
        const virtualScroller =
          container.querySelector('.MuiDataGrid-virtualScroller') ?? findScrollableDescendant(container)
        if (virtualScroller) {
          virtualScroller.addEventListener('scroll', handleScroll)
          return virtualScroller
        }
      }
      return null
    }

    let virtualScroller = setupScrollListener()

    let timeoutId: NodeJS.Timeout
    if (!virtualScroller) {
      timeoutId = setTimeout(() => {
        virtualScroller = setupScrollListener()
      }, 100)
    }

    return () => {
      if (virtualScroller) {
        virtualScroller.removeEventListener('scroll', handleScroll)
      }
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [hasNextPage, fetchNextPage, scrollThreshold])

  if (isLoading || (isFetching && !isFetchingNextPage))
    return (
      <Box
        height={height ?? '100%'}
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        {...fillBoxProps}
      >
        <CenteredSpinner height={spinnerHeight ?? (fitted ? '100%' : undefined)} />
      </Box>
    )

  if (data.length === 0 && !isLoading && emptyComponent) {
    return (
      <Box {...containerProps} {...fillBoxProps}>
        {emptyComponent}
      </Box>
    )
  }

  return (
    <Box
      ref={scrollContainerRef}
      height={fillSection ? '100%' : getInfiniteScrollTableHeight(enableAutoHeight, height)}
      width="100%"
      sx={{
        '& .MuiDataGrid-virtualScroller': {
          paddingBottom: bottomPadding,
        },
        '& .MuiDataGrid-virtualScrollerContent': {
          paddingBottom: bottomPadding,
        },
      }}
      {...containerProps}
      {...fillBoxProps}
    >
      <GenericTable
        id={id}
        columns={columns}
        defaultColumnOption={
          defaultColumnOption || columns.map(c => ({ field: c.field, headerName: c.headerName as string }))
        }
        data={data ?? []}
        enableColumnOptions={enableColumnOptions}
        onRowClick={onRowClick}
        autoHeight={enableAutoHeight}
        height={getInfiniteScrollTableHeight(enableAutoHeight, height)}
        getRowHeight={getRowHeight}
        rowsToFit={rowsToFit}
        loading={isLoading}
        updateFields={setFields}
        {...genericTableProps}
      />
    </Box>
  )
}

export default InfiniteScrollTable
